// The players' rebounding board: Crashing and Box-outs, the latest session / Last 10 / Season.
// A port of the design's "Rebounding Web.dc.html" (Claude Design project "rebounding dashboard"): markup, sizes and
// colours follow it as is. Additions for in-app use: a full-screen button (hides the app's nav bar, for casting or
// screenshots), a link to the upload page, Box-outs' SCORE / BOX-OUT % switch, and the viewer's columns (Trevor
// 2026-10-19): a column heading click makes that window primary, and COLUMNS shows or hides each column. Both are
// remembered per page in this browser.
import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { useLeagueApi } from "../../../data/LeagueEnvironmentProvider";
import { BoardRow, BoxoutMetric, build, Cell, headshotUrl, ICON, PAL, Window, WINDOWS } from "./reboundingModel";
import { ReboundingDashboard, ReboundingPage } from "./types";
import { GroundToggle, pill, RED, squareButton, store, stored, storedList, TitleBlock, useGround } from "./ui";
import "./rebounding.scss";

// A board column: one of the three windows, or the box-out % view's DRB / OREB allowed (the primary window's).
type Column = Window | "dreb" | "oreb";
const COLUMNS: Column[] = ["t", "l", "s", "dreb", "oreb"];
// Narrow and quiet (Trevor, 2026-10-19): small grey numbers under a small stacked heading, so they read as detail and
// barely move the board when the view switches between score and box-out %.
const DETAIL: { key: "dreb" | "oreb"; label: string; head: React.ReactNode; width: number }[] = [
  { key: "dreb", label: "DRB", head: "DRB", width: 34 },
  { key: "oreb", label: "ORB ALLOWED", head: <>ORB<br />ALLOWED</>, width: 52 },
];
const PAGES: ReboundingPage[] = ["crashing", "boxouts"];
const perPage = <T,>(read: (page: ReboundingPage) => T) =>
  Object.fromEntries(PAGES.map((p) => [p, read(p)])) as Record<ReboundingPage, T>;

// canUpload: the viewer may upload (DevTeam / ReboundingDashboardUsers); Coaches see the board without the UPLOAD link.
const Rebounding: React.FC<{ canUpload?: boolean }> = ({ canUpload = false }) => {
  const api = useLeagueApi();
  const [data, setData] = useState<ReboundingDashboard | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [mode, setMode] = useState<ReboundingPage>(() =>
    stored("reb-web-mode", ["crashing", "boxouts"] as const, "crashing"),
  );
  const [ground, setGround] = useGround();
  const [boxMetric, setBoxMetric] = useState<BoxoutMetric>(() =>
    stored("reb-web-boxout-metric", ["score", "pct"] as const, "score"),
  );
  const [width, setWidth] = useState(1280);
  const [hh, setHh] = useState(48);
  const [nameW, setNameW] = useState<number | null>(null);
  const [noPhoto, setNoPhoto] = useState<Set<string>>(() => new Set());
  const [fullscreen, setFullscreen] = useState(false);
  const [showHidden, setShowHidden] = useState(false);
  const [primaryBy, setPrimaryBy] = useState(() =>
    perPage((p) => stored<Window>(`reb-web-primary-${p}`, WINDOWS, "t")),
  );
  const [hiddenBy, setHiddenBy] = useState(() =>
    perPage((p) => storedList<Column>(`reb-web-hidden-columns-${p}`, COLUMNS)),
  );
  const [columnsOpen, setColumnsOpen] = useState(false);
  const rootRef = useRef<HTMLDivElement>(null);
  const hdrRef = useRef<HTMLDivElement>(null);
  const columnsRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    api
      .getReboundingDashboard()
      .then((d) => live && setData(d))
      .catch((e) => live && setError(e?.response?.data?.error || "The rebounding data could not be loaded."));
    return () => {
      live = false;
    };
  }, [api]);

  // The design sizes from the viewport; in the app the page's own box is the viewport.
  useLayoutEffect(() => {
    const root = rootRef.current;
    if (!root) return;
    const measure = () => {
      setWidth(root.clientWidth);
      if (hdrRef.current) setHh(Math.round(hdrRef.current.getBoundingClientRect().height));
    };
    const ro = new ResizeObserver(measure);
    ro.observe(root);
    if (hdrRef.current) ro.observe(hdrRef.current);
    measure();
    return () => ro.disconnect();
  }, []);

  // The name column is as wide as the widest first + last name, so the numbers sit right after the names.
  const measureNames = useCallback(() => {
    const els = rootRef.current?.querySelectorAll<HTMLElement>("[data-namecell]");
    if (!els || !els.length) return;
    let mx = 0;
    els.forEach((el) => {
      const kids = Array.from(el.children) as HTMLElement[];
      mx = Math.max(mx, Math.ceil(kids.reduce((a, c) => a + c.scrollWidth, 0) + 10 * (kids.length - 1)));
    });
    setNameW((cur) => (mx && Math.abs(mx - (cur || 0)) > 1 ? mx : cur));
  }, []);

  useLayoutEffect(() => {
    measureNames();
  }, [data, mode, boxMetric, width, primaryBy, hiddenBy, measureNames]);

  useEffect(() => {
    if (!columnsOpen) return;
    const away = (e: MouseEvent) => {
      if (!columnsRef.current?.contains(e.target as Node)) setColumnsOpen(false);
    };
    const esc = (e: KeyboardEvent) => e.key === "Escape" && setColumnsOpen(false);
    document.addEventListener("mousedown", away);
    document.addEventListener("keydown", esc);
    return () => {
      document.removeEventListener("mousedown", away);
      document.removeEventListener("keydown", esc);
    };
  }, [columnsOpen]);

  useEffect(() => {
    let live = true;
    document.fonts?.ready.then(() => live && measureNames());
    return () => {
      live = false;
    };
  }, [measureNames]);

  useEffect(() => {
    const onChange = () => setFullscreen(document.fullscreenElement === rootRef.current);
    document.addEventListener("fullscreenchange", onChange);
    return () => document.removeEventListener("fullscreenchange", onChange);
  }, []);

  const toggleFullscreen = () => {
    if (document.fullscreenElement) document.exitFullscreen();
    else rootRef.current?.requestFullscreen?.();
  };

  const isOff = mode !== "boxouts";
  const w = Math.min(width, 1680) - 48;
  const tight = w < 480;
  const primary = primaryBy[mode];
  const hidden = new Set(hiddenBy[mode]);

  const m = useMemo(
    () => (data ? build(data, mode, ground, showHidden, boxMetric, primary) : null),
    [data, mode, ground, showHidden, boxMetric, primary],
  );
  // The primary window first, the others in their usual order; the primary is never hidden. A narrow screen keeps
  // the primary and one more window, and drops the detail columns.
  const windows = [primary, ...WINDOWS.filter((x) => x !== primary && !hidden.has(x))].slice(0, w >= 540 ? 3 : 2);
  const details = m?.detail && w >= 640 ? DETAIL.filter((c) => !hidden.has(c.key)) : [];
  const cols = [
    "54px",
    nameW ? nameW + "px" : "minmax(100px,260px)",
    tight ? "136px" : "150px",
    ...details.map((c) => c.width + "px"),
    ...windows.slice(1).map(() => (tight ? "72px" : "minmax(76px,104px)")),
  ].join(" ");
  // Only upload groups receive hidden players (the API drops them for everyone else), so n is 0 for coaches.
  const hiddenCount = data?.players.filter((p) => p.hidden).length ?? 0;
  const P = m?.P ?? PAL[ground];
  const team = m?.team;
  const countSize = isOff || boxMetric === "pct" ? "17px" : "13px";
  const nameSize = tight ? "28px" : "36px";
  const todaySize = tight ? "32px" : "40px";
  const pick = (next: ReboundingPage) => {
    store("reb-web-mode", next);
    setMode(next);
  };
  const pickMetric = (next: BoxoutMetric) => {
    store("reb-web-boxout-metric", next);
    setBoxMetric(next);
  };
  const pickPrimary = (next: Window) => {
    store(`reb-web-primary-${mode}`, next);
    setPrimaryBy((cur) => ({ ...cur, [mode]: next }));
  };
  const toggleColumn = (c: Column) => {
    const next = hidden.has(c) ? hiddenBy[mode].filter((x) => x !== c) : [...hiddenBy[mode], c];
    store(`reb-web-hidden-columns-${mode}`, next.join(","));
    setHiddenBy((cur) => ({ ...cur, [mode]: next }));
  };
  const latest = m?.todayColumn ?? "LATEST";
  const heading = (x: Window) => m?.columns[x] ?? (x === "t" ? latest : x === "l" ? "LAST 10" : "SEASON");
  const label: React.CSSProperties = { fontSize: 11, fontWeight: 700, letterSpacing: ".12em", color: P.sub };
  const smallChip = (fill: string) => (
    <div style={{ width: 20, height: 20, background: fill, display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }} />
  );
  const bigCell = (c: Cell, key: string) => (
    <div key={key} style={{ height: 46, background: c.fill, border: c.border, display: "flex", alignItems: "center", gap: 6, padding: "0 8px", boxSizing: "border-box" }}>
      <span style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: todaySize, lineHeight: 1, color: c.ink, fontVariantNumeric: "tabular-nums" }}>{c.val}</span>
      <span style={{ marginLeft: "auto", fontSize: countSize, fontWeight: 700, color: c.ink, fontVariantNumeric: "tabular-nums" }}>{c.count}</span>
    </div>
  );
  const smallCell = (c: Cell, key: string) => (
    <div key={key} style={{ display: "flex", alignItems: "center", gap: 8 }}>
      {smallChip(c.fill)}
      <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 26, lineHeight: 1, color: c.ink, fontVariantNumeric: "tabular-nums" }}>{c.val}</span>
    </div>
  );
  // DRB / OREB allowed: plain detail beside the primary window, no band
  const detailCell = (v: string | undefined, key: string) => (
    <span key={key} style={{ fontFamily: "var(--font-display)", fontWeight: 700, fontSize: 17, lineHeight: 1, color: P.mute, fontVariantNumeric: "tabular-nums", textAlign: "center" }}>{v ?? "—"}</span>
  );
  const rowCells = (r: BoardRow) => [
    bigCell(r[windows[0]], "primary"),
    ...details.map((c) => detailCell(r.detail?.[c.key], c.key)),
    ...windows.slice(1).map((x) => smallCell(r[x], x)),
  ];
  // A window's heading: the primary one marked; the others make themselves primary on a click
  const headButton = (x: Window, isPrimary: boolean) => (
    <button
      key={x}
      className="reb-colhead"
      data-pick={isPrimary ? undefined : "1"}
      onClick={() => !isPrimary && pickPrimary(x)}
      title={isPrimary ? "The board is sorted by this column" : `Sort by ${heading(x).toLowerCase()}`}
      style={{ all: "unset", cursor: isPrimary ? "default" : "pointer", color: isPrimary ? P.fg : P.sub, whiteSpace: "nowrap", display: "inline-flex", alignItems: "center", gap: 6 }}
    >
      {heading(x)}
      {isPrimary && (
        <svg width="10" height="7" viewBox="0 0 10 7" aria-hidden="true">
          <path d="M0 0H10L5 7Z" fill={P.fg} />
        </svg>
      )}
    </button>
  );
  const toggleRow = (c: Column, text: string, locked: boolean) => {
    const on = locked || !hidden.has(c);
    return (
      <button
        key={c}
        className="reb-colhead"
        onClick={() => !locked && toggleColumn(c)}
        aria-pressed={on}
        disabled={locked}
        title={locked ? "The primary column is always shown" : undefined}
        style={{ all: "unset", display: "flex", alignItems: "center", gap: 10, cursor: locked ? "default" : "pointer", fontSize: 14, fontWeight: 700, letterSpacing: ".12em", color: locked ? P.mute : P.fg, whiteSpace: "nowrap" }}
      >
        <span style={{ width: 20, height: 20, border: `1px solid ${on ? (locked ? P.mute : P.fg) : P.pillBorder}`, background: on ? (locked ? P.mute : P.fg) : "transparent", display: "flex", alignItems: "center", justifyContent: "center", flex: "none" }}>
          {on && (
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke={P.bg} strokeWidth="3.4" strokeLinecap="square" aria-hidden="true">
              <path d={ICON.good} />
            </svg>
          )}
        </span>
        {text}
        {locked && <span style={{ fontSize: 11, color: P.mute }}>PRIMARY</span>}
      </button>
    );
  };

  return (
    <div ref={rootRef} className="rebounding" style={{ background: P.bg, color: P.fg }}>
      <div style={{ minHeight: "100%", background: P.bg, color: P.fg, display: "flex", flexDirection: "column" }}>
        <div ref={hdrRef} style={{ position: "sticky", top: 0, zIndex: 5, background: P.bg, borderBottom: `4px solid ${RED}` }}>
          <div style={{ maxWidth: 1680, margin: "0 auto", padding: "6px 24px", display: "flex", alignItems: "center", gap: 14, flexWrap: "wrap", boxSizing: "border-box" }}>
            <img src={P.logo} alt="Trail Blazers" style={{ height: 30, width: "auto" }} />
            <div style={{ display: "flex" }}>
              <button onClick={() => pick("crashing")} style={pill(P, isOff)}>CRASHING</button>
              <button onClick={() => pick("boxouts")} style={pill(P, !isOff)}>BOX-OUTS</button>
            </div>
            {!isOff && (
              <div style={{ display: "flex" }} title="What the Box-outs board shows">
                <button onClick={() => pickMetric("score")} style={pill(P, boxMetric === "score", 14)}>SCORE</button>
                <button onClick={() => pickMetric("pct")} style={pill(P, boxMetric === "pct", 14)}>BOX-OUT %</button>
              </div>
            )}
            <div style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 14 }}>
              <div ref={columnsRef} style={{ position: "relative" }}>
                <button onClick={() => setColumnsOpen((v) => !v)} style={pill(P, columnsOpen, 14)} aria-expanded={columnsOpen} title="Show or hide columns">
                  COLUMNS
                </button>
                {columnsOpen && (
                  <div role="dialog" aria-label="Columns" style={{ position: "absolute", right: 0, top: 40, zIndex: 10, background: P.bg, border: `1px solid ${P.pillBorder}`, padding: "12px 14px", display: "flex", flexDirection: "column", gap: 8, boxShadow: "0 6px 18px rgba(0,0,0,.18)" }}>
                    <span style={label}>SHOW</span>
                    {WINDOWS.map((x) => toggleRow(x, heading(x), x === primary))}
                    {m?.detail && DETAIL.map((c) => toggleRow(c.key, c.label, false))}
                    <span style={{ fontSize: 12, color: P.mute, marginTop: 2, whiteSpace: "nowrap" }}>Click a column heading to sort by it.</span>
                  </div>
                )}
              </div>
              {canUpload && hiddenCount > 0 && (
                <button onClick={() => setShowHidden((v) => !v)} style={pill(P, showHidden, 14)} title="Players hidden on the Players tab">
                  SHOW HIDDEN ({hiddenCount})
                </button>
              )}
              {canUpload && !fullscreen && (
                <Link to="/rebounding/upload" style={pill(P, false, 14)}>
                  UPLOAD
                </Link>
              )}
              <button
                onClick={toggleFullscreen}
                aria-label={fullscreen ? "Exit full screen" : "Full screen"}
                title={fullscreen ? "Exit full screen" : "Full screen"}
                style={squareButton(P)}
              >
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke={P.sub} strokeWidth="2.6" strokeLinecap="square">
                  <path d={fullscreen ? "M9 3v6H3M15 3v6h6M9 21v-6H3M15 21v-6h6" : "M3 9V3h6M21 9V3h-6M3 15v6h6M21 15v6h-6"} />
                </svg>
              </button>
              <GroundToggle ground={ground} setGround={setGround} />
            </div>
          </div>
        </div>

        <div style={{ maxWidth: 1680, width: "100%", margin: "0 auto", padding: "0 24px 48px", boxSizing: "border-box", display: "flex", flexDirection: "column" }}>
          <div style={{ display: "flex", flexWrap: "wrap", alignItems: "flex-end", justifyContent: "flex-start", gap: "8px 56px", padding: "6px 0 6px" }}>
            <TitleBlock
              eyebrow={(isOff ? "OFFENSE · REBOUNDING" : "DEFENSE · REBOUNDING") + " · " + (m?.todayLabel ?? "")}
              title={isOff ? "CRASHING" : "BOX-OUTS"}
            />
            <div style={{ display: "grid", gridTemplateColumns: windows.map(() => "auto").join(" "), columnGap: 18, rowGap: 2, alignItems: "center" }}>
              {windows.map((x, i) => (
                <span key={"h" + x} style={label}>
                  {i === 0 ? (m?.teamLabel ?? (isOff ? "TEAM" : "TEAM AVG")) + " · " + heading(x) : heading(x)}
                </span>
              ))}
              {windows.map((x, i) =>
                i === 0 ? (
                  <div key={x} style={{ height: 38, minWidth: 130, background: team?.[x].fill, display: "flex", alignItems: "center", gap: 6, padding: "0 10px", boxSizing: "border-box" }}>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 30, lineHeight: 1, color: team?.[x].ink, fontVariantNumeric: "tabular-nums" }}>{team?.[x].val}</span>
                    <span style={{ marginLeft: "auto", fontSize: 13, fontWeight: 700, color: team?.[x].ink, fontVariantNumeric: "tabular-nums" }}>{team?.[x].count}</span>
                  </div>
                ) : (
                  <div key={x} style={{ display: "flex", alignItems: "center", gap: 7 }}>
                    <div style={{ width: 18, height: 18, background: team?.[x].fill, flex: "none" }} />
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 24, lineHeight: 1, fontVariantNumeric: "tabular-nums" }}>{team?.[x].val}</span>
                  </div>
                ),
              )}
            </div>
          </div>

          <div style={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
            <div style={{ display: "grid", gridTemplateColumns: cols, columnGap: 14, justifyContent: "start", height: 30, alignItems: "end", paddingBottom: 5, borderBottom: `2px solid ${P.headRule}`, fontSize: 14, fontWeight: 700, letterSpacing: ".12em", color: P.sub, position: "sticky", top: hh, background: P.bg, zIndex: 4 }}>
              <span />
              <span>PLAYER</span>
              {headButton(windows[0], true)}
              {details.map((c) => (
                <span key={c.key} style={{ fontSize: 10, lineHeight: 1.15, letterSpacing: ".1em", color: P.mute, textAlign: "center" }}>{c.head}</span>
              ))}
              {windows.slice(1).map((x) => headButton(x, false))}
            </div>
            {m?.rows.map((r) => {
              const photo = r.nbaId && !noPhoto.has(r.nbaId) ? headshotUrl(r.nbaId) : null;
              return (
                <React.Fragment key={r.key}>
                  {r.heading && (
                    <div style={{ fontFamily: "var(--font-display)", fontWeight: 900, fontSize: 22, letterSpacing: ".12em", color: P.fg, padding: "18px 0 6px", borderBottom: `2px solid ${P.headRule}` }}>{r.heading}</div>
                  )}
                <div style={{ display: "grid", gridTemplateColumns: cols, columnGap: 14, minHeight: 46, justifyContent: "start", alignItems: "center", borderBottom: `1px solid ${P.rule}`, background: r.zebra, margin: "0 -10px", padding: "0 10px", opacity: r.hidden ? 0.4 : 1 }}>
                  <div style={{ width: 54, height: 40, background: P.photoBg, overflow: "hidden", display: "flex", alignItems: "center", justifyContent: "center" }}>
                    {photo ? (
                      <img
                        src={photo}
                        alt=""
                        onError={() => setNoPhoto((s) => new Set(s).add(r.nbaId!))}
                        style={{ width: 54, height: 40, objectFit: "cover", objectPosition: "top" }}
                      />
                    ) : (
                      <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: 20, color: P.photoInk }}>{r.initials}</span>
                    )}
                  </div>
                  <div data-namecell="1" style={{ minWidth: 0, maxWidth: "100%", width: "max-content", display: "flex", alignItems: "baseline", gap: 10 }}>
                    <span style={{ fontFamily: "var(--font-display)", fontWeight: 800, fontSize: nameSize, lineHeight: 1, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis", minWidth: 0, flex: "0 1 auto" }}>{r.first}</span>
                    {r.hidden && (
                      <span style={{ fontSize: 12, fontWeight: 700, letterSpacing: ".12em", color: P.sub, border: `1px solid ${P.pillBorder}`, padding: "1px 5px", flex: "none" }}>HIDDEN</span>
                    )}
                    {!tight && (
                      <span style={{ fontSize: 17, flex: "0 100 auto", minWidth: 0, fontWeight: 700, letterSpacing: ".1em", color: P.mute, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{r.last}</span>
                    )}
                  </div>
                  {rowCells(r)}
                </div>
                </React.Fragment>
              );
            })}
            {!m && (
              <div style={{ padding: "24px 0", fontSize: 17, color: P.sub }}>
                {error ?? "Loading…"}
              </div>
            )}
            {m && !m.rows.length && (
              <div style={{ padding: "24px 0", fontSize: 17, color: P.sub }}>
                No sessions yet this season. Upload a practice sheet to start the board.
              </div>
            )}
            {data?.meta.gamesError && isOff && (
              <div style={{ padding: "12px 0", fontSize: 14, color: P.mute }}>{data.meta.gamesError}</div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default Rebounding;
